import { useState, useCallback, useEffect } from "react";
import { useAuth } from "./useAuth";
import { useToast } from "./useToast";
import { NOTIFICATION_TYPES } from "../utils";
import { WIDGET_CONFIG_ENDPOINT } from "./useUsageWidgetConfig";

const METRICS_ENDPOINT = "usage/metrics/definitions/";
const DISCOVERED_EVENTS_ENDPOINT = "usage/discovered-events/";

export const useProductUsageConfig = (integrationId) => {
  const { apiClient } = useAuth();
  const showToast = useToast();

  // Metric definitions state
  const [metricDefinitions, setMetricDefinitions] = useState([]);
  const [metricsLoading, setMetricsLoading] = useState(true);

  // Widget config state
  const [widgetConfig, setWidgetConfig] = useState(null);
  const [widgetLoading, setWidgetLoading] = useState(true);
  const [widgetSaving, setWidgetSaving] = useState(false);

  // Available events state
  const [availableEvents, setAvailableEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  // Discovered events state
  const [discoveredEvents, setDiscoveredEvents] = useState([]);
  const [discoveredLoading, setDiscoveredLoading] = useState(false);
  const [promotingId, setPromotingId] = useState(null);
  const [dismissingId, setDismissingId] = useState(null);

  const [error, setError] = useState(null);

  // Fetch metric definitions (follows pagination to exhaustion)
  const fetchMetricDefinitions = useCallback(
    async (background = false) => {
      if (!apiClient) return;

      if (!background) setMetricsLoading(true);
      let allResults = [];
      try {
        let url = METRICS_ENDPOINT;
        let pages = 0;
        const MAX_PAGES = 20;

        while (url && pages < MAX_PAGES) {
          const response = await apiClient.get(url);
          allResults = [...allResults, ...(response.data.results || [])];
          url = response.data.next;
          if (url) {
            try {
              const parsed = new URL(url, window.location.origin);
              url = parsed.pathname.replace(/^\//, "") + parsed.search;
            } catch {
              url = null;
            }
          }
          pages++;
        }
        if (pages >= MAX_PAGES) {
          console.warn(
            `Metric definitions pagination capped at ${MAX_PAGES} pages (${allResults.length} items)`,
          );
        }
      } catch (err) {
        if (err.response?.status !== 404) {
          setError("Failed to load metric definitions");
        }
      } finally {
        setMetricDefinitions(allResults);
        setMetricsLoading(false);
      }
    },
    [apiClient],
  );

  // Fetch widget config
  const fetchWidgetConfig = useCallback(async () => {
    if (!apiClient) return;

    setWidgetLoading(true);
    try {
      const response = await apiClient.get(WIDGET_CONFIG_ENDPOINT);
      setWidgetConfig(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setWidgetConfig({});
      } else {
        setError("Failed to load widget configuration");
      }
    } finally {
      setWidgetLoading(false);
    }
  }, [apiClient]);

  // Fetch available Amplitude events
  const fetchAvailableEvents = useCallback(async () => {
    if (!apiClient || !integrationId) return;

    setEventsLoading(true);
    try {
      const response = await apiClient.get(
        `usage/integrations/${integrationId}/available-events/`,
      );
      setAvailableEvents(response.data.event_types || []);
    } catch (err) {
      console.error("Failed to fetch available events:", err);
    } finally {
      setEventsLoading(false);
    }
  }, [apiClient, integrationId]);

  // Fetch discovered events for this integration (follows pagination to exhaustion)
  const fetchDiscoveredEvents = useCallback(async () => {
    if (!apiClient || !integrationId) return;

    setDiscoveredLoading(true);
    let allResults = [];
    try {
      let url = `${DISCOVERED_EVENTS_ENDPOINT}?integration=${integrationId}`;
      let pages = 0;
      const MAX_PAGES = 20;

      while (url && pages < MAX_PAGES) {
        const response = await apiClient.get(url);
        allResults = [...allResults, ...(response.data.results || [])];
        url = response.data.next;
        if (url) {
          try {
            const parsed = new URL(url, window.location.origin);
            url = parsed.pathname.replace(/^\//, "") + parsed.search;
          } catch {
            url = null;
          }
        }
        pages++;
      }
      if (pages >= MAX_PAGES) {
        console.warn(
          `Discovered events pagination capped at ${MAX_PAGES} pages (${allResults.length} items)`,
        );
      }
    } catch (err) {
      console.error("Failed to fetch discovered events:", err);
    } finally {
      setDiscoveredEvents(allResults);
      setDiscoveredLoading(false);
    }
  }, [apiClient, integrationId]);

  // Create metric definition
  const createMetric = useCallback(
    async (data) => {
      try {
        const response = await apiClient.post(METRICS_ENDPOINT, data);
        setMetricDefinitions((prev) => [...prev, response.data]);
        showToast({
          content: "Metric created successfully",
          type: NOTIFICATION_TYPES.success,
        });
        return response.data;
      } catch (err) {
        showToast({
          content: err.response?.data?.error || "Failed to create metric",
          type: NOTIFICATION_TYPES.error,
        });
        throw err;
      }
    },
    [apiClient, showToast],
  );

  // Update metric definition
  const updateMetric = useCallback(
    async (id, data) => {
      try {
        const response = await apiClient.patch(
          `${METRICS_ENDPOINT}${id}/`,
          data,
        );
        setMetricDefinitions((prev) =>
          prev.map((m) => (m.id === id ? response.data : m)),
        );
        showToast({
          content: "Metric updated successfully",
          type: NOTIFICATION_TYPES.success,
        });
        return response.data;
      } catch (err) {
        showToast({
          content: err.response?.data?.error || "Failed to update metric",
          type: NOTIFICATION_TYPES.error,
        });
        throw err;
      }
    },
    [apiClient, showToast],
  );

  // Delete metric definition
  const deleteMetric = useCallback(
    async (id) => {
      try {
        await apiClient.delete(`${METRICS_ENDPOINT}${id}/`);
        setMetricDefinitions((prev) => prev.filter((m) => m.id !== id));
        showToast({
          content: "Metric deleted successfully",
          type: NOTIFICATION_TYPES.success,
        });
      } catch (err) {
        showToast({
          content: err.response?.data?.error || "Failed to delete metric",
          type: NOTIFICATION_TYPES.error,
        });
        throw err;
      }
    },
    [apiClient, showToast],
  );

  // Promote a discovered event to a metric definition
  const promoteEvent = useCallback(
    async (id) => {
      setPromotingId(id);
      try {
        const response = await apiClient.post(
          `${DISCOVERED_EVENTS_ENDPOINT}${id}/promote/`,
        );
        setDiscoveredEvents((prev) => prev.filter((e) => e.id !== id));
        setMetricDefinitions((prev) => [...prev, response.data]);
        showToast({
          content: `"${response.data.display_name}" is now a tracked metric`,
          type: NOTIFICATION_TYPES.success,
        });
        fetchMetricDefinitions(true);
        return response.data;
      } catch (err) {
        if (err.response?.status === 404) {
          setDiscoveredEvents((prev) => prev.filter((e) => e.id !== id));
          showToast({
            content: "This event was already processed",
            type: NOTIFICATION_TYPES.info,
          });
        } else {
          showToast({
            content: err.response?.data?.error || "Failed to track event",
            type: NOTIFICATION_TYPES.error,
          });
        }
      } finally {
        setPromotingId(null);
      }
    },
    [apiClient, showToast, fetchMetricDefinitions],
  );

  // Dismiss a discovered event
  const dismissEvent = useCallback(
    async (id) => {
      setDismissingId(id);
      try {
        await apiClient.post(`${DISCOVERED_EVENTS_ENDPOINT}${id}/dismiss/`);
        setDiscoveredEvents((prev) => prev.filter((e) => e.id !== id));
        showToast({
          content: "Event dismissed",
          type: NOTIFICATION_TYPES.success,
        });
      } catch (err) {
        showToast({
          content: err.response?.data?.error || "Failed to dismiss event",
          type: NOTIFICATION_TYPES.error,
        });
      } finally {
        setDismissingId(null);
      }
    },
    [apiClient, showToast],
  );

  // Save widget config
  const saveWidgetConfig = useCallback(
    async (config) => {
      setWidgetSaving(true);
      try {
        const response = await apiClient.put(WIDGET_CONFIG_ENDPOINT, config);
        setWidgetConfig(response.data);
        showToast({
          content: "Widget configuration saved",
          type: NOTIFICATION_TYPES.success,
        });
        return response.data;
      } catch (err) {
        showToast({
          content:
            err.response?.data?.error || "Failed to save widget configuration",
          type: NOTIFICATION_TYPES.error,
        });
        throw err;
      } finally {
        setWidgetSaving(false);
      }
    },
    [apiClient, showToast],
  );

  // Refetch all data
  const refetch = useCallback(() => {
    fetchMetricDefinitions();
    fetchWidgetConfig();
    fetchDiscoveredEvents();
  }, [fetchMetricDefinitions, fetchWidgetConfig, fetchDiscoveredEvents]);

  // Initial fetch
  useEffect(() => {
    fetchMetricDefinitions();
    fetchWidgetConfig();
  }, [fetchMetricDefinitions, fetchWidgetConfig]);

  useEffect(() => {
    if (integrationId) {
      fetchAvailableEvents();
      fetchDiscoveredEvents();
    }
  }, [integrationId, fetchAvailableEvents, fetchDiscoveredEvents]);

  return {
    metricDefinitions,
    metricsLoading,
    createMetric,
    updateMetric,
    deleteMetric,
    widgetConfig,
    widgetLoading,
    widgetSaving,
    saveWidgetConfig,
    availableEvents,
    eventsLoading,
    error,
    refetch,
    discoveredEvents,
    discoveredLoading,
    promoteEvent,
    dismissEvent,
    promotingId,
    dismissingId,
  };
};
